import { useState, useEffect, useRef } from 'react';

const LOCAL_STORAGE_CHANGE_EVENT = 'local_storage_change';

// Helper to dispatch custom events when storage changes
const notifyStorageChange = (key, value) => {
  window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, { 
    detail: { key, value } 
  }));
};


const useLocalStorage = (key, initialValue = null, expiryDefault = 60 * 60 * 1000) => {
  // Initialize state with stored value if it exists and hasn't expired
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const { token, expiresAt } = JSON.parse(stored);
        if (expiresAt === null || new Date().getTime() < expiresAt) {
          return token;
        }
        // If expired, clean up the storage
        localStorage.removeItem(key);
      }
      return initialValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return initialValue;
    }
  });

  // Keep track of expiry timer
  const expiryTimer = useRef(null);

  // Update local storage when value changes
  const setValue = (value, expiryDuration = expiryDefault) => {
    try {
      // Clear any existing expiry timer
      if (expiryTimer.current) {
        clearTimeout(expiryTimer.current);
        expiryTimer.current = null;
      }

      if (value === null) {
        // Case 2: User logged out
        localStorage.removeItem(key);
        setStoredValue(null);
        notifyStorageChange(key, null);
        return;
      }
      
      const expiresAt = expiryDuration === null ? null : new Date().getTime() + expiryDuration;

      const item = {
        token: value,
        expiresAt
      };
      localStorage.setItem(key, JSON.stringify(item));
      setStoredValue(value);
      notifyStorageChange(key, value);

      // Set timer for expiry notification (skip for infinite expiry)
      if (expiresAt !== null) {
        const timeUntilExpiry = expiresAt - new Date().getTime();
        expiryTimer.current = setTimeout(() => {
          localStorage.removeItem(key);
          setStoredValue(null);
          notifyStorageChange(key, null);
        }, timeUntilExpiry);
      }
    } catch (error) {
      console.error('Error writing to localStorage:', error);
      setStoredValue(value);
    }
  };

  // Listen for changes from other tabs/windows via the standard storage event
  // and also from our custom event for same-window updates
  useEffect(() => {
    // Handler for browser's storage event (cross-tab)
    const handleStorageChange = (e) => {
      if (e.key === key) {
        try {
          const newValue = e.newValue ? JSON.parse(e.newValue) : null;
          if (newValue) {
            const { token, expiresAt } = newValue;
            if (expiresAt === null || new Date().getTime() < expiresAt) {
              setStoredValue(token);
            } else {
              localStorage.removeItem(key);
              setStoredValue(null);
            }
          } else {
            setStoredValue(null);
          }
        } catch (error) {
          console.error('Error handling storage change:', error);
        }
      }
    };

    // Handler for custom events (same-window)
    const handleCustomEvent = (e) => {
      if (e.detail.key === key) {
        setStoredValue(e.detail.value);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleCustomEvent);
    
    // Clean up event listeners and timer on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleCustomEvent);
      if (expiryTimer.current) {
        clearTimeout(expiryTimer.current);
      }
    };
  }, [key]);

  return [storedValue, setValue];
};

export default useLocalStorage; 